import type { CityConfig } from "../city-template/types";

export const southPortlandConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { label: "Birth & Death Certificates — Maine CDC Vital Records", href: "https://www.maine.gov/dhhs/mecdc/public-health-systems/data-research/vital-records/", description: "Official Maine vital records office in Augusta" },
    { label: "Marriage Licenses — Cumberland County Clerk", href: "https://www.cumberlandcounty.org/", description: "Cumberland County Clerk — marriage license issuance" },
    { label: "Divorce Records — Cumberland County Clerk", href: "https://www.cumberlandcounty.org/", description: "Cumberland County Clerk — divorce decree records" },
    { label: "VitalChek — Order Vital Records Online", href: "https://www.vitalchek.com/", description: "Official partner of Maine CDC for online vital record orders" },
  ],
  city: "South Portland",
  state: "Maine",
  stateAbbr: "ME",
  slug: "south-portland",
  route: "/south-portland",
  title: "Emergency Passport South Portland, ME — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in South Portland, Maine — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for South Portland, Portland, and Cape Elizabeth travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in South Portland, ME",
  heroSubtitle: "Passport expired before your trip to Europe or Canada? Need an emergency passport fast? We help South Portland and Cumberland County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=South%20Portland%20Maine%20coastal%20waterfront%20with%20Portland%20Harbor%2C%20Spring%20Point%20Ledge%20Lighthouse%20visible%2C%20Casco%20Bay%20Islands%20in%20background%2C%20rocky%20shoreline%20with%20green%20grass%2C%20warm%20afternoon%20light%2C%20professional%20coastal%20photography%2C%20no%20visible%20text%20or%20people%2C%20clean%20and%20scenic%20New%20England%20harbor%20scene&width=1800&height=700&seq=south-portland-hero-001&orientation=landscape",
  heroImageAlt: "South Portland Maine coastal waterfront with Spring Point Ledge Lighthouse and Casco Bay",
  geoPosition: "43.6415;-70.2409",
  geoPlacename: "South Portland, Maine",
  lastModified: "2026-06-15",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "5 Custom House St, Boston, MA 02110",
    distance: "Located in Boston — approximately 110 miles (2 hours) from South Portland, ME",
  },
  nearbyCitiesZips: [
    { name: "South Portland", zips: "04106" },
    { name: "Cape Elizabeth", zips: "04107" },
    { name: "Scarborough", zips: "04074" },
    { name: "Portland", zips: "04101–04103" },
    { name: "Westbrook", zips: "04092" },
    { name: "Falmouth", zips: "04105" },
    { name: "Gorham", zips: "04038" },
    { name: "Yarmouth", zips: "04096" },
    { name: "Cumberland", zips: "04021" },
    { name: "North Yarmouth", zips: "04097" },
    { name: "Pownal", zips: "04069" },
    { name: "Freeport", zips: "04032" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent South Portland departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in Portland", route: "/portland-me" },
    { name: "Emergency passport help in Bangor", route: "/bangor" },
    { name: "Urgent passport services in Lewiston", route: "/lewiston" },
    { name: "Same-day passport help in Auburn", route: "/auburn-me" },
    { name: "Expedited passport services in Augusta", route: "/augusta-me" },
    { name: "Passport courier services in Brunswick", route: "/brunswick" },
    { name: "Rush passport help in Biddeford", route: "/biddeford" },
  ],
  fpcOfficeNote: "Fast Passport Center serves South Portland, Maine remotely via secure courier. We do not have a walk-in office in South Portland — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for South Portland travelers.",
  fpcOfficeAddress: "One Boston Place, #2600, Boston, MA 02108",
  fpcOfficeDistance: "Serving all of Cumberland County",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout South Portland and the surrounding coastal communities. From the waterfront neighborhoods to the Maine Mall area, from Cape Elizabeth to Scarborough — we help South Portland travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all South Portland neighborhoods and nearby cities including Cape Elizabeth, Scarborough, Portland, Westbrook, and Falmouth. If you are in Cumberland County or nearby York County, we have you covered.",
  howItWorksIntro: "Most South Portland travelers complete everything online and ship documents overnight. No need to drive to Boston or wait in line at the regional agency. Our emergency and expedited passport process is designed for Maine travelers who need fast, reliable results.",
  mistakesIntro: "With South Portland being a key residential hub for Portland-area travelers, passport delays can be costly. Here are the most common mistakes we see.",
  testimonialsIntro: "South Portland travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in South Portland? Here are the most common ones we hear from Maine travelers.",
  statePageRoute: "/states/maine",
  statePageName: "Maine",
  dmvWebsite: "https://www.maine.gov/sos/bmv",
  dmvLabel: "Maine BMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent South Portland international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Jennifer M.",
      location: "South Portland, ME",
      rating: 5,
      text: "My flight to London was in 4 days and my passport had expired. Fast Passport Center got me a new one in under 48 hours. I shipped from the South Portland UPS Store and had my passport the next day.",
      service: "Emergency Passport",
    },
    {
      name: "Mark R.",
      location: "Cape Elizabeth, ME",
      rating: 5,
      text: "I needed to renew my passport for a business trip to Toronto. They reviewed my documents, caught a mistake I would have missed, and I had my passport in 5 days. Worth every penny.",
      service: "Passport Renewal",
    },
    {
      name: "Linda S.",
      location: "Scarborough, ME",
      rating: 5,
      text: "Lost my passport a week before my honeymoon to Italy. I was panicking. Fast Passport Center handled the DS-64, got me a replacement, and I had it in time. Truly a lifesaver.",
      service: "Lost Passport",
    },
    {
      name: "Paul D.",
      location: "Westbrook, ME",
      rating: 5,
      text: "As a frequent business traveler from the Portland area, I need a reliable passport service. Fast Passport Center has been my go-to for years. Professional, fast, and always dependable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "sp-1",
      name: "South Portland Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "799 Broadway",
      city: "South Portland",
      state: "ME",
      zip: "04106",
      phone: "(207) 767-5126",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from South Portland center",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "sp-2",
      name: "Portland Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "125 Forest Ave",
      city: "Portland",
      state: "ME",
      zip: "04101",
      phone: "(207) 871-8391",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "2.5 miles from South Portland",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Portland-side residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "sp-3",
      name: "Cape Elizabeth Town Office",
      type: "Town Office Passport Acceptance",
      address: "320 Ocean House Rd",
      city: "Cape Elizabeth",
      state: "ME",
      zip: "04107",
      phone: "(207) 767-3201",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "3 miles from South Portland",
      website: "https://www.capeelizabeth.com/",
      websiteLabel: "Cape Elizabeth Town Office",
      notes: "Good option for Cape Elizabeth residents. Appointment recommended.",
      icon: "ri-government-line",
    },
    {
      id: "sp-4",
      name: "Scarborough Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "37 Pleasant Hill Rd",
      city: "Scarborough",
      state: "ME",
      zip: "04074",
      phone: "(207) 883-5124",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "4 miles from South Portland",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Accepts walk-ins but appointments preferred. Photo services not available on-site.",
      icon: "ri-mail-line",
    },
    {
      id: "sp-5",
      name: "Westbrook Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1041 Main St",
      city: "Westbrook",
      state: "ME",
      zip: "04092",
      phone: "(207) 854-2811",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "5 miles from South Portland",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Small office with limited hours. Call ahead to schedule passport acceptance.",
      icon: "ri-mail-line",
    },
    {
      id: "sp-6",
      name: "Falmouth Town Office",
      type: "Town Office Passport Acceptance",
      address: "271 Falmouth Rd",
      city: "Falmouth",
      state: "ME",
      zip: "04105",
      phone: "(207) 781-5253",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "8 miles from South Portland",
      website: "https://www.falmouthme.org/",
      websiteLabel: "Falmouth Town Office",
      notes: "Popular for Falmouth and surrounding towns. Appointment recommended.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — drive to Boston", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Maine photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at South Portland acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many South Portland applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Boston regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many South Portland families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "South Portland travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in South Portland." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your South Portland address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in South Portland, Maine?",
      a: "Fast Passport Center offers emergency passport processing in South Portland in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays. This is the fastest reliable option for South Portland travelers with urgent international flights.",
    },
    {
      q: "Can I get a same-day passport in South Portland, Maine?",
      a: "Same-day passports are only available at the Boston Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to South Portland residents.",
    },
    {
      q: "Where can I get a fast passport renewal in South Portland, Maine?",
      a: "Fast Passport Center offers fast passport renewal in South Portland with no office visit required. We serve all South Portland neighborhoods and surrounding areas including Cape Elizabeth, Scarborough, and Portland. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "What is the fastest way to get an urgent passport in South Portland, Maine?",
      a: "The fastest way to get an urgent passport in South Portland is through Fast Passport Center's emergency courier service. Our registered couriers deliver your documents directly to State Department processing facilities, achieving 24–48 hour turnaround. This is significantly faster than standard mail processing (8–11 weeks) or even government expedited mail (3–5 weeks).",
    },
    {
      q: "How fast can I get a passport in South Portland, Maine?",
      a: "With Fast Passport Center's emergency courier service, South Portland travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to visit the Boston Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in South Portland?",
      a: "Popular options include the South Portland Post Office (04106), Portland Main Post Office (04101), Cape Elizabeth Town Office (04107), and Scarborough Post Office (04074). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "What if I live in Cape Elizabeth or Scarborough?",
      a: "We serve all of Cumberland County including Cape Elizabeth, Scarborough, Portland, Westbrook, and Falmouth. Our overnight shipping labels work from any UPS location, so your location within the Portland metro doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in South Portland?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in South Portland?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and UPS Store locations in South Portland and Portland offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in South Portland?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in South Portland?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk South Portland families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — South Portland", address: "430 Main St, South Portland, ME 04106", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — South Portland", address: "645 Main St, South Portland, ME 04106", hours: "Mon–Sun: 7AM–11PM" },
    { name: "UPS Store — South Portland", address: "225 Western Ave, South Portland, ME 04106", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — South Portland", address: "60 Cottage Rd, South Portland, ME 04106", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Cape Elizabeth", address: "354 Ocean House Rd, Cape Elizabeth, ME 04107", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Scarborough", address: "425 Payne Rd, Scarborough, ME 04074", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Portland", address: "510 Forest Ave, Portland, ME 04101", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Westbrook", address: "700 Main St, Westbrook, ME 04092", hours: "Mon–Sun: 7AM–10PM" },
  ],
  dmvLocations: [
    {
      name: "BMV — South Portland",
      address: "36 Mollison Way, Portland, ME 04103",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "BMV — Cape Elizabeth",
      address: "320 Ocean House Rd, Cape Elizabeth, ME 04107",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "BMV — Scarborough",
      address: "500 Route 1, Scarborough, ME 04074",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "BMV — Westbrook",
      address: "3 Idaka Ln, Westbrook, ME 04092",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service center. Online appointment system available.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "PWM",
      name: "Portland International Jetport",
      distance: "5 miles from South Portland",
      address: "1001 Westbrook St, Portland, ME 04102",
      note: "Maine's busiest airport with direct flights to Canada, Florida, and major U.S. hubs. International connections through Boston, NYC, and Philadelphia.",
      flightUrl: "https://www.portlandjetport.org/",
      flightLabel: "PWM Flight Info",
      featured: true,
    },
    {
      code: "BOS",
      name: "Logan International Airport",
      distance: "110 miles from South Portland (2 hours)",
      address: "1 Harborside Dr, Boston, MA 02128",
      note: "Major international hub with direct flights to Europe, Asia, and Latin America. Many South Portland travelers connect through Boston for international travel.",
      flightUrl: "https://www.massport.com/logan-airport",
      flightLabel: "Logan Airport",
      featured: false,
    },
    {
      code: "BGR",
      name: "Bangor International Airport",
      distance: "130 miles from South Portland (2.5 hours)",
      address: "2870 Godfrey Blvd, Bangor, ME 04401",
      note: "Smaller airport with domestic flights and some seasonal international routes. Alternative for northern Maine travelers.",
      flightUrl: "https://www.flybangor.com/",
      flightLabel: "Bangor Airport",
      featured: false,
    },
  ],
};